/**
 * UltraLife Trades — Trade Configurations
 *
 * Defines the four trade verticals: Decks, Concrete Flatwork, Painting, Roofing.
 * Each trade has its own materials, quote builder steps, pricing, and AI prompt logic.
 */

// ─── DECKS ───
const DECK_MATERIALS = [
  { id: "trex-enhance", name: "Trex Enhance", type: "Composite", pricePerSqFt: 12, swatch: "#b39573",
    colors: [
      { name: "Clam Shell", hex: "#d4c5b0" }, { name: "Beach Dune", hex: "#c4a97d" },
      { name: "Saddle", hex: "#8b6f4e" }, { name: "Coastal Bluff", hex: "#9e8e7a" },
    ]},
  { id: "trex-select", name: "Trex Select", type: "Composite", pricePerSqFt: 15, swatch: "#9e8367",
    colors: [
      { name: "Saddle", hex: "#8b6f4e" }, { name: "Madeira", hex: "#6b4a30" },
      { name: "Pebble Grey", hex: "#a89e92" }, { name: "Winchester Grey", hex: "#7a7268" },
    ]},
  { id: "trex-transcend", name: "Trex Transcend", type: "Composite", pricePerSqFt: 22, swatch: "#7a5c3a",
    colors: [
      { name: "Spiced Rum", hex: "#7a5231" }, { name: "Tiki Torch", hex: "#a67c52" },
      { name: "Lava Rock", hex: "#4a3828" }, { name: "Gravel Path", hex: "#8a7e6e" }, { name: "Havana Gold", hex: "#b08840" },
    ]},
  { id: "timbertech", name: "TimberTech AZEK", type: "PVC", pricePerSqFt: 25, swatch: "#6b4a30",
    colors: [
      { name: "Dark Hickory", hex: "#5a3a20" }, { name: "Coastline", hex: "#8a7e6e" }, { name: "Vintage Mahogany", hex: "#6b3a28" },
    ]},
  { id: "cedar-decking", name: "Western Red Cedar", type: "Natural Wood", pricePerSqFt: 14, swatch: "#c49a6c",
    colors: [
      { name: "Natural", hex: "#c49a6c" }, { name: "Sealed", hex: "#b38b5d" }, { name: "Stained", hex: "#8b6f4e" },
    ]},
  { id: "pressure-treated", name: "Pressure-Treated Pine", type: "Natural Wood", pricePerSqFt: 7, swatch: "#8fbc8f",
    colors: [
      { name: "Natural", hex: "#8fbc8f" }, { name: "Stained Brown", hex: "#7a6040" },
    ]},
  { id: "composite-generic", name: "Composite Decking", type: "Composite", pricePerSqFt: 13, swatch: "#a08868",
    colors: [
      { name: "Driftwood", hex: "#a08868" }, { name: "Espresso", hex: "#5a3a20" }, { name: "Grey", hex: "#8a8a8a" }, { name: "Natural Cedar", hex: "#c49a6c" },
    ]},
];

const DECK_RAILING_OPTIONS = [
  { id: "wood", name: "Wood Railing", pricePerLf: 35, swatch: "#b39573" },
  { id: "cable", name: "Cable Railing", pricePerLf: 65, swatch: "#c0c0c0" },
  { id: "glass", name: "Glass Panel", pricePerLf: 95, swatch: "#d4e8f0" },
  { id: "metal", name: "Metal Balusters", pricePerLf: 55, swatch: "#888888" },
  { id: "composite", name: "Composite Railing", pricePerLf: 45, swatch: "#a0826d" },
  { id: "none", name: "No Railing", pricePerLf: 0, swatch: null },
];

const DECK_EXTRAS = [
  { id: "lighting", name: "Deck Lighting Package", basePrice: 1800 },
  { id: "pergola", name: "Shade Pergola", basePrice: 4500 },
  { id: "benches", name: "Built-in Seating", basePrice: 1200 },
  { id: "hottub", name: "Hot Tub Reinforcement", basePrice: 2000 },
  { id: "firepit", name: "Fire Pit Integration", basePrice: 3500 },
  { id: "privacy", name: "Privacy Screen", basePrice: 1800 },
];

// Per-unit pricing for trade-specific addon inputs (used in TradesPlatform.jsx)
export const ADDON_PRICING = {
  // Deck
  DECK_ICE_WATER_PER_SQFT: 1.50,
  DECK_STAIRS_PER_FLIGHT: 2000,
  // Concrete (border price varies by finish)
  CONCRETE_BORDER_BROOM_PER_LF: 15,
  CONCRETE_BORDER_EXPOSED_PER_LF: 20,
  CONCRETE_BORDER_STAMPED_PER_LF: 28,
  CONCRETE_BORDER_COLORED_PER_LF: 22,
  CONCRETE_BORDER_POLISHED_PER_LF: 18,
  CONCRETE_CURBS_PER_LF: 18,
  CONCRETE_GUTTERS_PER_LF: 14,
  CONCRETE_STEPS_EACH: 700,
  // Painting
  PAINTING_DOORS_EACH: 120,
  PAINTING_WINDOWS_EACH: 80,
  PAINTING_HVAC_VENTS_EACH: 25,
  PAINTING_CEILING_PER_SQFT: 1.50,
  PAINTING_TRIM_BASEBOARD_PER_LF: 3,
  PAINTING_TRIM_EXTERIOR_PER_LF: 4,
  PAINTING_SHUTTERS_EACH: 90,
  PAINTING_FASCIA_SOFFIT_PER_LF: 5,
  PAINTING_GARAGE_DOORS_EACH: 200,
  // Roofing
  ROOFING_ICE_WATER_PER_SQFT: 1.75,
  ROOFING_RIDGE_VENT_PER_LF: 12,
  ROOFING_DRIP_EDGE_PER_LF: 3,
  ROOFING_FLASHING_EACH: 250,
  ROOFING_SKYLIGHTS_EACH: 600,
  ROOFING_GUTTERS_PER_LF: 12,
};

// ─── CONCRETE FLATWORK ───
const CONCRETE_FINISHES = [
  { id: "broom", name: "Broom Finish", type: "Standard", pricePerSqFt: 8, swatch: "#c8beb4",
    colors: [{ name: "Natural Grey", hex: "#c8beb4" }, { name: "Charcoal Tint", hex: "#8a8a8a" }]},
  { id: "exposed-aggregate", name: "Exposed Aggregate", type: "Decorative", pricePerSqFt: 11, swatch: "#b8a898",
    colors: [{ name: "River Pebble", hex: "#b8a898" }, { name: "Buff Pebble", hex: "#c4b07a" }, { name: "White Quartz", hex: "#e8e0d0" }]},
  { id: "stamped", name: "Stamped Concrete", type: "Decorative", pricePerSqFt: 15, swatch: "#c4a87a",
    colors: [{ name: "Sandstone", hex: "#c4a87a" }, { name: "Slate Grey", hex: "#8a8078" }, { name: "Adobe Tan", hex: "#c8a870" }, { name: "Charcoal", hex: "#5a5a5a" }]},
  { id: "colored", name: "Integral Color", type: "Decorative", pricePerSqFt: 12, swatch: "#b89060",
    colors: [{ name: "Buff", hex: "#c8a870" }, { name: "Terracotta", hex: "#c46040" }, { name: "Charcoal", hex: "#5a5a5a" }]},
  { id: "polished", name: "Polished / Grind & Seal", type: "Premium", pricePerSqFt: 14, swatch: "#d0c8c0",
    colors: [{ name: "Natural", hex: "#d0c8c0" }, { name: "Charcoal Grout", hex: "#7a7a7a" }]},
];

const CONCRETE_EXTRAS = [
  { id: "tearout", name: "Existing Concrete Tearout", basePrice: 1500 },
  { id: "sealer", name: "Penetrating Sealer Application", basePrice: 600 },
  { id: "borders", name: "Decorative Borders", basePrice: 800 },
  { id: "drainage", name: "Drain & Channel Install", basePrice: 1200 },
  { id: "steps", name: "Concrete Steps (3-step)", basePrice: 1400 },
  { id: "curbs", name: "Curb & Gutter", basePrice: 900 },
];

// ─── PAINTING ───
const PAINTING_SERVICES = [
  { id: "exterior-full", name: "Exterior — Full House", type: "Exterior", pricePerSqFt: 2.5, swatch: "#e8d8c0",
    colors: [{ name: "Prep + 2 Coats", hex: "#e8d8c0" }, { name: "Prep + 3 Coats", hex: "#d4c0a8" }]},
  { id: "exterior-paint-only", name: "Exterior — Paint Only (no prep)", type: "Exterior", pricePerSqFt: 1.8, swatch: "#d4c4a8",
    colors: [{ name: "1 Coat", hex: "#ddd0bc" }, { name: "2 Coats", hex: "#d4c4a8" }]},
  { id: "interior-full", name: "Interior — Full House (walls + ceilings)", type: "Interior", pricePerSqFt: 2.0, swatch: "#f0e8e0",
    colors: [{ name: "Flat Ceiling", hex: "#f8f4f0" }, { name: "Eggshell Walls", hex: "#f0e8e0" }, { name: "Satin Trim", hex: "#e0d8d0" }]},
  { id: "interior-rooms", name: "Interior — Per Room Package", type: "Interior", pricePerSqFt: 2.8, swatch: "#e8e0d8",
    colors: [{ name: "Flat", hex: "#f0ece8" }, { name: "Eggshell", hex: "#e8e0d8" }, { name: "Satin", hex: "#e0d8cc" }]},
  { id: "cabinet-refinish", name: "Cabinet Refinishing", type: "Interior", pricePerSqFt: 0, swatch: "#c8a870",
    colors: [{ name: "White / Off-White", hex: "#f4f0e8" }, { name: "Navy / Dark", hex: "#2a3a5a" }, { name: "Natural Wood", hex: "#c8a870" }]},
  { id: "deck-stain", name: "Deck Stain / Seal", type: "Exterior", pricePerSqFt: 1.5, swatch: "#a07040",
    colors: [{ name: "Natural", hex: "#c8a870" }, { name: "Semi-Transparent Brown", hex: "#a07040" }, { name: "Solid Stain", hex: "#7a5030" }]},
];

const PAINTING_EXTRAS = [
  { id: "primer", name: "Full Primer Coat (exterior)", basePrice: 800 },
  { id: "trim-windows", name: "Trim & Window Surrounds", basePrice: 1200 },
  { id: "garage-door", name: "Garage Door Painting", basePrice: 400 },
  { id: "power-wash", name: "Power Washing (pre-paint)", basePrice: 350 },
  { id: "caulk-seal", name: "Caulk & Seal All Gaps", basePrice: 500 },
  { id: "fence", name: "Fence Staining / Painting", basePrice: 900 },
  { id: "accent-wall", name: "Accent Wall (interior)", basePrice: 300 },
];

// ─── ROOFING ───
const ROOFING_MATERIALS = [
  { id: "arch-shingles-std", name: "Architectural Shingles (Std)", type: "Asphalt", pricePerSqFt: 5.5, swatch: "#6a6a6a",
    colors: [{ name: "Charcoal", hex: "#4a4a4a" }, { name: "Weathered Wood", hex: "#8a7860" }, { name: "Barkwood", hex: "#7a6040" }, { name: "Driftwood", hex: "#a09080" }]},
  { id: "arch-shingles-class4", name: "Architectural Shingles (Class 4 Impact)", type: "Asphalt", pricePerSqFt: 7.0, swatch: "#5a5a5a",
    colors: [{ name: "Charcoal", hex: "#4a4a4a" }, { name: "Weathered Wood", hex: "#8a7860" }, { name: "Barkwood", hex: "#7a6040" }]},
  { id: "metal-standing-seam", name: "Standing Seam Metal", type: "Metal", pricePerSqFt: 14.0, swatch: "#8a9098",
    colors: [{ name: "Charcoal Grey", hex: "#6a6a70" }, { name: "Galvalume", hex: "#c0c0c0" }, { name: "Dark Bronze", hex: "#4a3828" }, { name: "Barn Red", hex: "#8a2020" }]},
  { id: "metal-exposed-fastener", name: "Metal Roofing (Exposed Fastener)", type: "Metal", pricePerSqFt: 9.0, swatch: "#a0a8b0",
    colors: [{ name: "Galvalume", hex: "#c0c0c0" }, { name: "Charcoal", hex: "#5a5a60" }, { name: "Green", hex: "#406040" }]},
  { id: "cedar-shake", name: "Cedar Shake / Shingles", type: "Wood", pricePerSqFt: 10.0, swatch: "#c49a6c",
    colors: [{ name: "Natural", hex: "#c49a6c" }, { name: "Preservative-Treated", hex: "#a07040" }]},
  { id: "flat-tpo", name: "TPO (Flat / Low Slope)", type: "Membrane", pricePerSqFt: 8.0, swatch: "#e0e0e0",
    colors: [{ name: "White", hex: "#f0f0f0" }, { name: "Grey", hex: "#b0b0b0" }]},
];

const ROOFING_EXTRAS = [
  { id: "decking-replace", name: "Decking / Sheathing Replacement", basePrice: 1800 },
  { id: "ice-water", name: "Full Ice & Water Shield Upgrade", basePrice: 900 },
  { id: "ridge-vent", name: "Ridge Vent Installation", basePrice: 600 },
  { id: "gutters", name: "Gutter Replacement (per 100 lf)", basePrice: 1200 },
  { id: "skylights", name: "Skylight Flashing / Replace", basePrice: 800 },
  { id: "chimney-flash", name: "Chimney Flashing", basePrice: 700 },
  { id: "ventilation", name: "Attic Ventilation Upgrade", basePrice: 1000 },
];

// ─── TRADE DEFINITIONS ───
export const TRADES = {
  decks: {
    slug: "decks",
    name: "Decks",
    displayName: "Custom Decks",
    description: "Design your perfect outdoor deck. We build with the best materials for your local climate.",
    icon: "🪵",
    sizeLabel: "Approximate Deck Size (sq ft)",
    sizeMin: 100, sizeMax: 2000, sizeDefault: 400, sizeStep: 25,
    sizeDescriptions: { min: "100 sf — small balcony", max: "2,000 sf — wrap-around" },
    steps: ["Size & Photos", "Materials", "Railings & Extras", "Your Info"],
    materials: DECK_MATERIALS,
    railingOptions: DECK_RAILING_OPTIONS,
    extras: DECK_EXTRAS,
    hasRailing: true,
    laborRate: 18, // $/sqft
    chatGreeting: "Hi! I'm here to help you plan your deck project. What are you thinking — new build, replacement, or repairs?",
    chatPlaceholder: "Ask about materials, costs, timelines...",
    submitConfirmation: "Thanks! We'll review your project details and get back to you within 24 hours with a detailed estimate.",
    pricingNote: "Approximate range. Includes materials, labor, and overhead. Does not include permits, engineering, or site-specific conditions. Exact pricing confirmed after site visit.",
    aiPersonality: "You're a knowledgeable, friendly deck expert — direct and helpful, not salesy. You know local building codes, materials, and what holds up in this climate.",
  },

  concrete: {
    slug: "concrete",
    name: "Concrete",
    displayName: "Concrete Flatwork",
    description: "Driveways, patios, sidewalks, and decorative concrete. Long-lasting results for your local climate.",
    icon: "🏗️",
    sizeLabel: "Project Area (sq ft)",
    sizeMin: 100, sizeMax: 3000, sizeDefault: 500, sizeStep: 50,
    sizeDescriptions: { min: "100 sf — small patio", max: "3,000 sf — large driveway" },
    steps: ["Size & Photos", "Finish Type", "Add-Ons", "Your Info"],
    materials: CONCRETE_FINISHES,
    railingOptions: null,
    extras: CONCRETE_EXTRAS,
    hasRailing: false,
    laborRate: 5, // $/sqft base
    chatGreeting: "Hi! I can help you plan your concrete project — driveway, patio, sidewalk, or decorative work. What are you working on?",
    chatPlaceholder: "Ask about finishes, costs, timelines...",
    submitConfirmation: "Thanks! We'll review your project and get back to you within 24 hours with a detailed estimate.",
    pricingNote: "Approximate range. Includes prep, pour, and finish. Does not include permits, soil remediation, or site-specific grading. Exact pricing confirmed after site visit.",
    aiPersonality: "You're a knowledgeable concrete flatwork expert — helpful, direct, and familiar with local soil and climate conditions that affect concrete longevity.",
  },

  painting: {
    slug: "painting",
    name: "Painting",
    displayName: "Interior & Exterior Painting",
    description: "Professional painting that protects your home and transforms your space.",
    icon: "🎨",
    sizeLabel: "Total Paintable Area (sq ft)",
    sizeMin: 200, sizeMax: 5000, sizeDefault: 1200, sizeStep: 100,
    sizeDescriptions: { min: "200 sf — single room", max: "5,000 sf — full exterior" },
    steps: ["Area & Photos", "Service Type", "Add-Ons", "Your Info"],
    materials: PAINTING_SERVICES,
    railingOptions: null,
    extras: PAINTING_EXTRAS,
    hasRailing: false,
    laborRate: 0, // included in pricePerSqFt
    chatGreeting: "Hi! I can help you plan your painting project — interior, exterior, or both. What are you thinking?",
    chatPlaceholder: "Ask about prep, products, timelines...",
    submitConfirmation: "Thanks! We'll review your project details and get back to you within 24 hours with a quote.",
    pricingNote: "Approximate range. Includes labor, materials, and prep. Exact pricing confirmed after walkthrough.",
    aiPersonality: "You're a professional painter with expertise in prep work, product selection, and what holds up in this climate. Helpful and specific, not vague.",
  },

  roofing: {
    slug: "roofing",
    name: "Roofing",
    displayName: "Roofing",
    description: "Full roof replacements, repairs, and storm damage.",
    icon: "🏠",
    sizeLabel: "Roof Size (sq ft of footprint)",
    sizeMin: 800, sizeMax: 4000, sizeDefault: 1500, sizeStep: 100,
    sizeDescriptions: { min: "800 sf — small home", max: "4,000 sf — large home" },
    steps: ["Roof Size & Photos", "Material", "Add-Ons", "Your Info"],
    materials: ROOFING_MATERIALS,
    railingOptions: null,
    extras: ROOFING_EXTRAS,
    hasRailing: false,
    laborRate: 3, // $/sqft tear-off base
    chatGreeting: "Hi! I can help you plan your roofing project — full replacement, repair, or storm damage assessment. What's going on with your roof?",
    chatPlaceholder: "Ask about materials, storm damage, timelines...",
    submitConfirmation: "Thanks! We'll review your project details and get back to you within 24 hours. For active leaks, please call us directly.",
    pricingNote: "Approximate range based on footprint. Includes tear-off, materials, and install. Does not include structural repairs, permits, or unknown decking damage. Exact pricing confirmed after inspection.",
    aiPersonality: "You're a roofing expert who understands local weather patterns, hail zones, snow load requirements, and insurance documentation. Direct and specific.",
  },
};

/** Get trade config by slug, returns null if not found */
export function getTrade(slug) {
  if (!slug) return null;
  return TRADES[slug.toLowerCase()] || null;
}

/** Default trade */
export const DEFAULT_TRADE = TRADES.decks;

// ─── PRICING HELPERS ───

/**
 * Calculate estimate for decks (most complex trade)
 */
export function calculateDeckEstimate({ sqft, material, railing, railingLf = 60, extras = [] }) {
  const mat = material ? material.pricePerSqFt * sqft : 0;
  const rail = railing && railing.id !== "none" ? railing.pricePerLf * railingLf : 0;
  const ext = extras.reduce((s, e) => s + (e.basePrice || 0), 0);
  const labor = TRADES.decks.laborRate * sqft;
  const subtotal = mat + rail + ext + labor;
  const overhead = subtotal * 0.15;
  return {
    total: Math.round(subtotal + overhead),
    breakdown: { materials: Math.round(mat), labor: Math.round(labor), railing: Math.round(rail), extras: Math.round(ext), overhead: Math.round(overhead) },
  };
}

/**
 * Generic estimate calculator for all trades
 */
export function calculateEstimate({ trade, sqft, material, railing, railingLf = 0, extras = [] }) {
  const tradeConfig = typeof trade === "string" ? TRADES[trade] : trade;
  if (!tradeConfig) return { total: 0, breakdown: {} };

  const mat = material ? material.pricePerSqFt * sqft : 0;
  const rail = railing && railing.id !== "none" ? (railing.pricePerLf || 0) * railingLf : 0;
  const ext = extras.reduce((s, e) => s + (e.basePrice || 0), 0);
  const labor = tradeConfig.laborRate * sqft;
  const subtotal = mat + rail + ext + labor;
  const overhead = subtotal * 0.15;

  return {
    total: Math.round(subtotal + overhead),
    low: Math.round((subtotal + overhead) * 0.9),
    high: Math.round((subtotal + overhead) * 1.1),
    breakdown: {
      materials: Math.round(mat),
      labor: Math.round(labor),
      railing: Math.round(rail),
      extras: Math.round(ext),
      overhead: Math.round(overhead),
    },
  };
}
